import {
  ExtendedShareholderDTO,
  totalNumberOfShares,
  totalNumberOfSharesOfShareholder,
} from "@/types/Shareholder";
import { ApiService, ShareholderCommitmentDetailsResponse } from "@/api";
import { makeAutoObservable } from "mobx";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { combineDateAndTime, toDate } from "@/utils/Dates";
import { History } from "history";
import { navigateToShareholderCommitmentDetails } from "@/utils/navigation";
import { failure, isOk, ok, pending, Result } from "@/common/common";
import { groupBy, identity, maxBy, sortBy, sum } from "lodash";

import { TMessageKey } from "@/i18n/types.gen";
import { FileUploadConfig, toFormData, validateFiles } from "@/fileUpload/fileUploadDomain";
import { TFunction, TMessage } from "@/i18n/tt";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  ShareholderCommitment,
  ShareholderCommitmentDetails,
  ShareholderCommitmentType,
  shareholderCommitmentTypes,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { ButtonProps } from "@/components-v2/Button";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import { DataTableProps } from "@/components-v2/DataTable";
import { ShareTypeCode } from "@/common/share";

export class ShareholderCommitmentModel {
  singleCommitingShareholder: RadioFieldModel<ExtendedShareholderDTO | null>;
  multipleCommitingShareholders: CommitmentPerShareholderModel[];
  amount: NumberFieldModel;
  registrationDate: LocalDateFieldModel;
  recallableAmount: NumberFieldModel;
  comment: StringFieldModel | null = null;
  formMode = new RadioFieldModel<FormMode>({
    value: "single",
    options: [...formModes],
    idFn: identity,
    labelFn: formModeLabel,
  });
  error?: TMessage;
  isLoading: boolean = false;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly organisationNumber: string,
    readonly currency: string,
    readonly history: History,
    readonly shareholders: ExtendedShareholderDTO[],
    readonly totalNumberOfShares: number,
    readonly commitmentType: ShareholderCommitmentType,
    readonly commitments?: Result<ShareholderCommitment[]>,
  ) {
    this.singleCommitingShareholder = new RadioFieldModel({
      value: this.availableShareholders.length > 0 ? this.availableShareholders[0] : null,
      options: this.availableShareholders,
      idFn: (s) => String(s?.id),
      labelFn: (s) => ({ translated: s?.name ?? "" }),
      required: true,
    });
    this.multipleCommitingShareholders = sortBy(this.availableShareholders, "name").map(
      (s) =>
        new CommitmentPerShareholderModel(
          s,
          this.currency,
          this.isDraw ? this.overviewDataForShareholder(s.id) : undefined,
        ),
    );
    this.amount = new NumberFieldModel({
      required: true,
      isPositive: true,
      validator: (value) => {
        const data =
          this.singleCommitingShareholder.value !== null
            ? this.overviewDataForShareholder(this.singleCommitingShareholder.value.id)
            : undefined;
        if (this.isDraw && data !== undefined && data.remaining < (value ?? 0)) {
          return {
            key: "shareholder-commitments.error.greater-than-remaining",
            params: { amount: { type: "amount", value: data.remaining, currency: currency } },
          };
        }
      },
    });
    this.recallableAmount = new NumberFieldModel({
      value: 0,
      isPositiveOrZero: true,
      validator: (value) =>
        this.amount.toNumberOrZero() >= (value ?? 0)
          ? undefined
          : {
              key: "transactions.validation-messages.max",
              params: {
                n: { type: "number", value: this.amount.toNumberOrZero() },
              },
            },
    });
    this.registrationDate = new LocalDateFieldModel({ required: true });
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  get availableShareholders() {
    return this.isDraw ? this.shareholdersWithRemainingCommitment : this.shareholders;
  }

  get isDraw() {
    return this.commitmentType === "DRAW";
  }

  get isSingleShareholderMode() {
    return this.formMode.value === "single";
  }

  addComment() {
    this.comment = new StringFieldModel();
  }

  get shareholdersWithRemainingCommitment() {
    return this.shareholders.filter((shareholder) =>
      this.commitmentOverviewData.some(
        (commitment) => commitment.id === shareholder.id && commitment.remaining > 0,
      ),
    );
  }

  get commitmentOverviewData(): ShareholderCommitmentData[] {
    return isOk(this.commitments)
      ? prepareCommitmentOverviewRows(
          this.commitments.data,
          this.shareholders,
          this.totalNumberOfShares,
        )
      : [];
  }

  overviewDataForShareholder(shareholderId: number) {
    return this.commitmentOverviewData.find((d) => d.id === shareholderId);
  }

  get isValid() {
    return (
      (this.isSingleShareholderMode
        ? this.singleCommitingShareholder.isValid &&
          this.amount.isValid &&
          this.recallableAmount.isValid
        : this.multipleCommitingShareholders.every((s) => s.isValid) &&
          this.multipleCommitingShareholders.filter((s) => s.amount.parsedValue).length > 0) &&
      this.registrationDate.isValid
    );
  }

  validate() {
    this.error = undefined;
    if (this.isSingleShareholderMode) {
      this.singleCommitingShareholder.validate();
      this.amount.validate();
      this.recallableAmount.validate();
    } else {
      this.multipleCommitingShareholders.forEach((s) => s.validate());
      if (this.multipleCommitingShareholders.filter((s) => s.amount.parsedValue).length === 0) {
        this.error = { key: "shareholder-commitments.error.no-amount" };
      }
    }
    this.registrationDate.validate();

    return this.isValid;
  }

  *submit() {
    if (!this.validate()) return;
    this.setLoading(true);
    try {
      const commmitmentId: string = yield this.apiService.registerShareholderCommitment({
        organisationNumber: this.organisationNumber,
        type: this.commitmentType,
        registrationDate: combineDateAndTime(this.registrationDate.validatedValue!, "12:00"),
        comment: this.comment?.validatedValue ?? null,
        details: this.isSingleShareholderMode
          ? [
              {
                shareholderId: this.singleCommitingShareholder.value!.id,
                currency: this.currency,
                recallableAmount: this.recallableAmount.parsedValue,
                amount: this.amount.parsedValue!,
              },
            ]
          : this.multipleCommitingShareholders
              .filter((s) => s.amount.parsedValue)
              .map((s) => ({
                shareholderId: s.shareholder.id,
                currency: this.currency,
                recallableAmount: s.recallableAmount.parsedValue,
                amount: s.amount.parsedValue!,
              })),
      });

      navigateToShareholderCommitmentDetails(this.organisationNumber, commmitmentId, this.history);
      this.setLoading(false);
      this.messageStore.showSuccess({ key: "shareholder-commitments.register-success" });
    } catch (e) {
      this.setLoading(false);
      this.messageStore.showError({ key: "shareholder-commitments.register-error" });
    }
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
}

class CommitmentPerShareholderModel {
  amount: NumberFieldModel;
  recallableAmount: NumberFieldModel;

  constructor(
    readonly shareholder: ExtendedShareholderDTO,
    readonly currency: string,
    readonly data?: ShareholderCommitmentData,
  ) {
    this.amount = new NumberFieldModel({
      isPositiveOrZero: true,
      validator: (value) =>
        data !== undefined && data.remaining < (value ?? 0)
          ? {
              key: "shareholder-commitments.error.greater-than-remaining",
              params: { amount: { type: "amount", value: data.remaining, currency: currency } },
            }
          : undefined,
    });
    this.recallableAmount = new NumberFieldModel({
      value: 0,
      isPositiveOrZero: true,
      validator: (value) =>
        this.amount.toNumberOrZero() < (value ?? 0)
          ? {
              key: "transactions.validation-messages.max",
              params: { n: { type: "number", value: this.amount.toNumberOrZero() } },
            }
          : undefined,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validate() {
    this.amount.validate();
    this.recallableAmount.validate();
  }

  get isValid() {
    return this.amount.isValid && this.recallableAmount.isValid;
  }
}

const formModes = ["single", "multiple"] as const;

type FormMode = (typeof formModes)[number];

function formModeLabel(mode: FormMode): TMessage {
  switch (mode) {
    case "single":
      return { key: "shareholder-commitments.form-mode.single" };
    case "multiple":
      return { key: "shareholder-commitments.form-mode.multiple" };
  }
}

export function parseCommitmentType(value: string): ShareholderCommitmentType {
  const type = shareholderCommitmentTypes.find((type) => type === value.toUpperCase());
  return type ?? "COMMITMENT";
}

export type CommitmentRow = {
  id: string;
  type: ShareholderCommitmentType;
  description: string;
  registrationDate: string;
  updatedDate: string;
};

export class ShareholderCommitmentController {
  fetchShareholderCommitmentsResult?: Result<ShareholderCommitment[]>;
  fetchSingleShareholderCommitmentResult?: Result<ShareholderCommitmentDetailsResponse>;
  textFilter: StringFieldModel = new StringFieldModel();
  typeFilter: RadioFieldModel<ShareholderCommitmentType | null> = new RadioFieldModel({
    value: null,
    options: [null, ...shareholderCommitmentTypes],
    idFn: (s) => s ?? "ALL",
    labelFn: (s) => ({
      key: s ? shareholderCommitmentTypeLabel(s) : "shareholder-commitments.type.all",
    }),
  });
  changeDateForCommitmentId?: string;
  changeDateModel: LocalDateFieldModel = new LocalDateFieldModel();
  submitChangeDateResult?: Result<TMessage, TMessage>;
  deleteCommitmentId?: string;
  submitDeleteResult?: Result<TMessage, TMessage>;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly organisationNumber: string,
    shareholderCommitments?: ShareholderCommitment[],
  ) {
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
    this.fetchShareholderCommitmentsResult = shareholderCommitments
      ? ok(shareholderCommitments)
      : undefined;
  }

  *fetchShareholderCommitments() {
    this.fetchShareholderCommitmentsResult = pending();
    try {
      const commitments: ShareholderCommitment[] =
        yield this.apiService.fetchShareholderCommitments(this.organisationNumber);
      this.fetchShareholderCommitmentsResult = ok(commitments);
    } catch (e) {
      this.fetchShareholderCommitmentsResult = failure();
    }
  }

  *fetchSingleShareholderCommitment(commitmentId: string) {
    this.fetchSingleShareholderCommitmentResult = pending();
    try {
      const response: ShareholderCommitmentDetailsResponse =
        yield this.apiService.fetchSingleShareholderCommitments(
          this.organisationNumber,
          commitmentId,
        );
      this.fetchSingleShareholderCommitmentResult = ok(response);
    } catch (e) {
      this.fetchSingleShareholderCommitmentResult = failure();
    }
  }

  findShareholderData(
    shareholder: ExtendedShareholderDTO | null,
    shares: {
      [shareTypeCode in ShareTypeCode]?: number;
    },
  ) {
    if (isOk(this.fetchShareholderCommitmentsResult) && shareholder) {
      return prepareShareholderData(
        this.fetchShareholderCommitmentsResult.data,
        shareholder,
        totalNumberOfShares(shares),
      );
    }
  }

  *handleUpload(
    files: File[],
    commitment: ShareholderCommitment,
    fileUploadConfig: FileUploadConfig,
  ) {
    yield validateFiles(files, fileUploadConfig);
    yield this.apiService.uploadCommitmentAttachments(
      this.organisationNumber,
      commitment.shareholderCommitmentId,
      toFormData(files),
    );

    this.fetchSingleShareholderCommitment(commitment.shareholderCommitmentId);
  }

  downloadAttachment(commitmentId: string, attachmentId: string) {
    this.apiService.downloadCommitmentAttachment(
      this.organisationNumber,
      commitmentId,
      attachmentId,
    );
  }

  *deleteAttachment(commitment: ShareholderCommitment, attachmentId: string) {
    yield this.apiService.deleteCommitmentAttachment(
      this.organisationNumber,
      commitment.shareholderCommitmentId,
      attachmentId,
    );
    this.fetchSingleShareholderCommitment(commitment.shareholderCommitmentId);
  }

  showChangeDateDialogForCommitment(commitmentId: string) {
    this.changeDateForCommitmentId = commitmentId;
    this.changeDateModel = new LocalDateFieldModel();
  }

  get isShowingChangeDateDialog() {
    return this.changeDateForCommitmentId !== undefined;
  }

  hideChangeDateDialog() {
    this.changeDateForCommitmentId = undefined;
    this.submitChangeDateResult = undefined;
  }

  *submitChangeDate() {
    this.submitChangeDateResult = pending();
    try {
      yield this.apiService.changeCommitmentDate(
        this.organisationNumber,
        this.changeDateForCommitmentId!,
        combineDateAndTime(this.changeDateModel.validatedValue!, "12:00"),
      );

      this.submitChangeDateResult = ok({ key: "ok" });
      this.hideChangeDateDialog();
      this.fetchShareholderCommitments();
    } catch (e) {
      this.submitChangeDateResult = failure({ key: "shareholder-commitments.change-date-error" });
    }
  }

  showDeleteConfirmationDialogForCommitment(commitmentId: string) {
    this.deleteCommitmentId = commitmentId;
  }

  get isShowingDeleteConfirmationDialog() {
    return this.deleteCommitmentId !== undefined;
  }

  hideDeleteConfirmationDialog() {
    this.deleteCommitmentId = undefined;
    this.submitDeleteResult = undefined;
  }

  *submitDelete() {
    this.submitDeleteResult = pending();
    try {
      yield this.apiService.deleteCommitment(this.organisationNumber, this.deleteCommitmentId!);

      this.submitDeleteResult = ok({ key: "shareholder-commitments.delete-success" });
      this.hideChangeDateDialog();
      this.fetchShareholderCommitments();
    } catch (e) {
      this.submitDeleteResult = failure({ key: "shareholder-commitments.delete-error" });
    }
  }
}

export function prepareCommitmentRows(
  commitments: ShareholderCommitment[],
  shareholders: ExtendedShareholderDTO[],
  t: TFunction,
): CommitmentRow[] {
  const shareholderMap = groupBy(shareholders, "id");
  return commitments
    .filter((commitment) => commitment.details.length > 0)
    .map((commitment) => {
      const amount = commitment.details.reduce((sum, detail) => sum + detail.amount, 0);
      return {
        id: commitment.shareholderCommitmentId,
        type: commitment.type,
        description: t({
          key: commitmentDescriptionLabel(commitment),
          params: {
            name: shareholderMap[commitment.details[0].shareholderId]?.[0]?.name,
            amount: { type: "amount", value: amount, currency: commitment.details[0].currency },
            count: commitment.details.length,
          },
        }),
        registrationDate: commitment.registrationDate,
        updatedDate: commitment.updatedAt,
      };
    });
}

export function shareholderCommitmentTypeLabel(
  commitmentType: ShareholderCommitmentType,
): TMessageKey {
  switch (commitmentType) {
    case "DRAW":
      return "shareholder-commitments.type.DRAW";
    case "COMMITMENT":
      return "shareholder-commitments.type.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-commitments.type.DISTRIBUTION";
  }
}

export function commitmentRegisterLabel(commitmentType: ShareholderCommitmentType): TMessageKey {
  switch (commitmentType) {
    case "DRAW":
      return "shareholder-commitments.register.DRAW";
    case "COMMITMENT":
      return "shareholder-commitments.register.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-commitments.register.DISTRIBUTION";
  }
}

export function commitmentAttachmentLabel(commitmentType: ShareholderCommitmentType): TMessageKey {
  switch (commitmentType) {
    case "DRAW":
      return "shareholder-commitments.info-attachments.DRAW";
    case "COMMITMENT":
      return "shareholder-commitments.info-attachments.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-commitments.info-attachments.DISTRIBUTION";
  }
}

export function filterCommitmentRows(
  rows: CommitmentRow[],
  filter: string,
  commitmentType: ShareholderCommitmentType | null,
): CommitmentRow[] {
  return rows.filter(
    (row) =>
      Object.values(row).some((value) => value.toLowerCase().includes(filter)) &&
      (commitmentType === null || commitmentType === row.type),
  );
}

export type ShareholderCommitmentSubpage = "overview" | "log";

export type ShareholderCommitmentData = {
  id: number;
  shareholder: string;
  ownership: number;
  committed: number;
  drawn: number;
  distributed: number;
  recallable: number;
  remaining: number;
};

export type ShareholderCommitmentTotalData = {
  committed: number;
  drawn: number;
  distributed: number;
  recallable: number;
  remaining: number;
  currency: string;
  lastRegistrationDate?: string;
};

export function prepareCommitmentOverviewRows(
  commitments: ShareholderCommitment[],
  shareholders: ExtendedShareholderDTO[],
  sharesTotal: number,
): ShareholderCommitmentData[] {
  const commitmentsByType = groupBy(commitments, "type");
  const commitmentDetailsMap = detailsByShareholder(commitmentsByType["COMMITMENT"]);
  const drawnDetailsMap = detailsByShareholder(commitmentsByType["DRAW"]);
  const distributionDetailsMap = detailsByShareholder(commitmentsByType["DISTRIBUTION"]);
  return shareholders
    .filter(
      (s) => commitmentDetailsMap[s.id] || drawnDetailsMap[s.id] || distributionDetailsMap[s.id],
    )
    .map((s) =>
      calculateShareholderCommitmentData(
        commitmentDetailsMap[s.id],
        drawnDetailsMap[s.id],
        distributionDetailsMap[s.id],
        s,
        sharesTotal,
      ),
    );
}

export function prepareCommitmentOverviewTotal(
  commitments: ShareholderCommitment[],
  data: ShareholderCommitmentData[],
  currency: string,
): ShareholderCommitmentTotalData {
  return {
    committed: data.reduce((sum, commitment) => sum + commitment.committed, 0),
    drawn: data.reduce((sum, commitment) => sum + commitment.drawn, 0),
    distributed: data.reduce((sum, commitment) => sum + commitment.distributed, 0),
    recallable: data.reduce((sum, commitment) => sum + commitment.recallable, 0),
    remaining: data.reduce((sum, commitment) => sum + commitment.remaining, 0),
    currency,
    lastRegistrationDate: getLastRegisteredCommitment(commitments)?.registrationDate,
  };
}

export function getLastRegisteredCommitment(
  commitments: ShareholderCommitment[],
): ShareholderCommitment | undefined {
  return maxBy(commitments, (x) => toDate(x.registrationDate));
}

export function prepareShareholderData(
  commitments: ShareholderCommitment[],
  shareholder: ExtendedShareholderDTO,
  sharesTotal: number,
): ShareholderCommitmentData {
  const commitmentsByType = groupBy(commitments, "type");
  const commitmentDetailsMap = detailsByShareholder(commitmentsByType["COMMITMENT"]);
  const drawnDetailsMap = detailsByShareholder(commitmentsByType["DRAW"]);
  const distributionDetailsMap = detailsByShareholder(commitmentsByType["DISTRIBUTION"]);
  return calculateShareholderCommitmentData(
    commitmentDetailsMap[shareholder.id],
    drawnDetailsMap[shareholder.id],
    distributionDetailsMap[shareholder.id],
    shareholder,
    sharesTotal,
  );
}

function detailsByShareholder(commitments?: ShareholderCommitment[]) {
  if (!commitments) return {};
  const detailsForType = commitments.flatMap((c) => c.details);
  return groupBy(detailsForType, "shareholderId");
}

export function calculateShareholderCommitmentData(
  commitmentDetails: ShareholderCommitmentDetails[] | undefined,
  drawnDetails: ShareholderCommitmentDetails[] | undefined,
  distributionDetails: ShareholderCommitmentDetails[] | undefined,
  shareholder: ExtendedShareholderDTO,
  sharesTotal: number,
): ShareholderCommitmentData {
  const committed = sumCommitmentAmount(commitmentDetails);
  const drawn = sumCommitmentAmount(drawnDetails);
  const distributed = sumCommitmentAmount(distributionDetails);
  const recallable = distributionDetails
    ? sum(distributionDetails.map((c) => c.recallableAmount ?? 0))
    : 0;
  const remaining = committed - drawn + recallable;
  return {
    id: shareholder.id,
    shareholder: shareholder.name,
    ownership: totalNumberOfSharesOfShareholder(shareholder) / sharesTotal,
    committed: committed,
    drawn,
    distributed,
    recallable,
    remaining: remaining > 0 ? remaining : 0,
  };
}

export function sumCommitmentAmount(details?: ShareholderCommitmentDetails[]) {
  return details ? details.map((c) => c.amount).reduce((a, b) => a + b, 0) : 0;
}

export function commitmentDescriptionLabel(commitment: ShareholderCommitment): TMessageKey {
  switch (commitment.type) {
    case "DRAW":
      return "shareholder-commitments.description.DRAW";
    case "COMMITMENT":
      return "shareholder-commitments.description.COMMITMENT";
    case "DISTRIBUTION":
      return "shareholder-commitments.description.DISTRIBUTION";
  }
}

export const prepareShareholderCommitmentsEmptyState = ({
  action,
}: {
  action?: ButtonProps;
}): EmptyStateProps => ({
  illustration: "card-holder",
  title: { key: "shareholder-commitments.no-contributions-registered" },
  description: { key: "shareholder-commitments.get-started" },
  actions: action ? [{ type: "button", key: "action", props: action }] : undefined,
});

export const prepareShareholderCommitmentsTable = ({
  data,
  canEdit,
  navigateToDetails,
  changeDate,
  deleteCommitment,
}: {
  data: CommitmentRow[];
  canEdit: boolean;
  navigateToDetails?: (id: string) => void;
  changeDate?: (id: string) => void;
  deleteCommitment?: (id: string) => void;
}): DataTableProps<CommitmentRow> => ({
  tableData: data,
  tableColumns: [
    {
      name: "type",
      label: { key: "shareholder-commitments.type" },
      align: "left",
      getValue: (row) => ({ key: shareholderCommitmentTypeLabel(row.type) }),
    },
    {
      name: "description",
      label: { key: "shareholder-commitments.description" },
      align: "left",
    },
    {
      name: "registrationDate",
      label: { key: "shareholder-commitments.registered" },
      align: "left",
      getSortValue: (row) => toDate(row.registrationDate).getTime(),
      getValue: (row) => ({ type: "date", value: row.registrationDate }),
    },
    {
      name: "updatedDate",
      label: { key: "shareholder-commitments.lastUpdated" },
      align: "left",
      getSortValue: (row) => toDate(row.updatedDate),
      getValue: (row) => ({ type: "date", value: row.updatedDate }),
    },
  ],
  getId: (row) => row.id,
  rowActions: canEdit
    ? (row) => [
        [
          "context-menu",
          [
            {
              type: "item",
              label: { key: "shareholder-commitments.change-date" },
              onSelect: () => changeDate?.(row.id),
              icon: "calendar",
            },
            {
              type: "item",
              label: { key: "shareholder-commitments.delete" },
              onSelect: () => deleteCommitment?.(row.id),
              icon: "trash",
              color: "error",
            },
          ],
        ],
      ]
    : undefined,
  onRowClick: canEdit && navigateToDetails ? (row) => navigateToDetails(row.id) : undefined,
});
